import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { GraduationCap, CheckCircle2, Clock, AlertTriangle, FileText, ArrowLeft, Languages, Lock, Download, Volume2, Pause, Loader2, MessageCircle } from 'lucide-react';

export default function MyTrainingPage() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('pending');
  const [openSop, setOpenSop] = useState(null);
  const navigate = useNavigate();

  const refresh = () => {
    api.get('/me/training').then(r => setData(r.data)).catch(() => {});
  };

  useEffect(refresh, []);

  // Auto-open SOP if ?open={sopId} is in URL (WhatsApp share → landing experience)
  useEffect(() => {
    if (!data?.pending && !data?.completed) return;
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    if (!openId) return;
    const all = [...(data.pending || []), ...(data.completed || [])];
    const match = all.find((x) => x.sop_id === openId || x.id === openId);
    if (match) {
      setOpenSop(match);
      // Clean URL so refresh doesn't reopen forever
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [data]);

  if (!data) return <div className="p-6 text-sm" style={{ color: '#6B7280' }}>Loading your training…</div>;
  if (data.no_person_link) {
    return (
      <div className="max-w-xl mx-auto p-6 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>No training profile linked</p>
        <p className="text-xs mt-1" style={{ color: '#6B7280' }}>Your login isn't connected to a person profile yet. Ask Dhiraj or Dipali to link you so SOPs can be assigned.</p>
      </div>
    );
  }

  const list = tab === 'pending' ? data.pending : data.completed;
  const overdueCount = data.pending.filter(p => p.overdue).length;

  // Does the user have any LCG (Les Concierges / Club Concierge) training modules?
  // If so, render the Wave-grouped programme view above the flat Pending/Completed list.
  const allItems = [...(data.pending || []), ...(data.completed || [])];
  const lcgItems = allItems.filter(it => !!it.module_code && (it.module_code || '').startsWith('LCG'));
  const hasLcg = lcgItems.length > 0;

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="my-training-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs" style={{ color: '#378ADD' }}>
        <ArrowLeft size={12} /> Back
      </button>

      <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F7F5F2' }}>
            <GraduationCap size={22} style={{ color: '#2A7B88' }} />
          </div>
          <div className="flex-1">
            <p className="text-base font-bold" style={{ color: '#1B2A4A' }}>My Training</p>
            <p className="text-xs" style={{ color: '#6B7280' }}>{data.person.name} · {data.person.role} · {data.person.outlet}</p>
          </div>
          <div className="flex gap-3">
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: '#D4726A' }}>{data.counts.pending}</p>
              <p className="text-[10px]" style={{ color: '#6B7280' }}>Pending</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: '#2D8B5E' }}>{data.counts.completed}</p>
              <p className="text-[10px]" style={{ color: '#6B7280' }}>Read</p>
            </div>
          </div>
        </div>

        {overdueCount > 0 && (
          <div className="mt-3 rounded-md p-2.5 flex items-center gap-2" style={{ backgroundColor: '#FEF2F2' }}>
            <AlertTriangle size={14} style={{ color: '#991B1B' }} />
            <p className="text-xs font-semibold" style={{ color: '#991B1B' }}>{overdueCount} SOP{overdueCount === 1 ? '' : 's'} overdue (&gt;7 days since assigned)</p>
          </div>
        )}

        {data.counts.pending > 0 && (
          <div className="mt-3 h-2 rounded-full" style={{ backgroundColor: '#F3F4F6' }}>
            <div className="h-full rounded-full" style={{
              width: `${(data.counts.completed / data.counts.total) * 100}%`,
              backgroundColor: '#2D8B5E',
            }} />
          </div>
        )}
      </div>

      {/* Les Concierges / Club Concierge training programme — wave-grouped view.
          Only appears for LC/CC staff who have LCG modules assigned.
          Soft-recommend order: Wave 1 first (with "Start here" badges), no hard locks. */}
      {hasLcg && (
        <ConciergeProgramme items={lcgItems} onOpen={setOpenSop} />
      )}

      {/* Tab switcher */}
      <div className="flex gap-1 border-b" style={{ borderColor: '#E5E1DC' }}>
        {[
          { id: 'pending', label: `Pending · ${data.counts.pending}` },
          { id: 'completed', label: `Completed · ${data.counts.completed}` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-xs font-medium border-b-2 ${tab === t.id ? 'border-[#2A7B88]' : 'border-transparent'}`}
            style={{ color: tab === t.id ? '#1B2A4A' : '#6B7280' }}
            data-testid={`tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="bg-white rounded-xl border p-6 text-center" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#1B2A4A' }}>
            {tab === 'pending' ? "Nothing pending — you're all caught up! 🎉" : 'Nothing acknowledged yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map(item => (
            <button key={item.assignment_id} onClick={() => setOpenSop(item)}
              className="w-full text-left bg-white rounded-xl border p-3 hover:bg-[#F7F5F2] transition flex items-start gap-3"
              style={{ borderColor: item.overdue ? '#FCA5A5' : '#E5E1DC', borderWidth: item.overdue ? 2 : 1 }}
              data-testid={`my-sop-${item.sop_id}`}>
              {tab === 'pending'
                ? <div className="w-5 h-5 mt-0.5 rounded-full border-2 flex-shrink-0" style={{ borderColor: item.overdue ? '#991B1B' : '#D4726A' }} />
                : <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" style={{ color: '#2D8B5E' }} />
              }
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium" style={{ color: '#1B2A4A' }}>{item.title}</p>
                <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>
                  {item.category} · {item.outlet_name}
                  {item.media_count > 0 ? ` · ${item.media_count} media` : ''}
                  {item.has_document ? ' · document' : ''}
                </p>
                <p className="text-[10px] mt-1 flex items-center gap-1" style={{ color: item.overdue ? '#991B1B' : '#9CA3AF' }}>
                  {tab === 'pending' ? (
                    <>
                      <Clock size={9} />
                      {item.overdue
                        ? `${item.days_since_assigned} days ago · OVERDUE`
                        : `Assigned ${item.days_since_assigned || 0} day${item.days_since_assigned === 1 ? '' : 's'} ago`}
                    </>
                  ) : (
                    <>Read on {(item.acknowledged_at || '').slice(0, 10)}</>
                  )}
                </p>
              </div>
              <span className="text-[10px]" style={{ color: '#378ADD' }}>Open →</span>
            </button>
          ))}
        </div>
      )}

      {openSop && <SopReadModal item={openSop} onClose={() => { setOpenSop(null); refresh(); }} />}
    </div>
  );
}


// ─── SOP read modal with language toggle, watermark, audio tracking and "I've read this" button ─────

/** Inline "🔊 Listen" button that streams ElevenLabs TTS for a translation. */
function SopAudioPlayer({ translationId, langLabel }) {
  const [state, setState] = useState('idle'); // idle | loading | playing | error
  const [audio] = useState(() => new Audio());
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    // Stop + reset whenever translation changes
    try { audio.pause(); } catch (_) { /* no-op */ }
    audio.src = '';
    setState('idle');
    return () => {
      try { audio.pause(); } catch (_) { /* no-op */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationId]);

  useEffect(() => {
    const onEnded = () => setState('idle');
    const onError = () => { setState('error'); setErrorMsg('Playback failed'); };
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, [audio]);

  const toggle = async () => {
    if (state === 'playing') {
      audio.pause();
      setState('idle');
      return;
    }
    if (audio.src) {
      try { await audio.play(); setState('playing'); } catch (e) { setState('error'); setErrorMsg(String(e)); }
      return;
    }
    // First tap — fetch the stream with credentials, create blob URL
    setState('loading'); setErrorMsg('');
    try {
      const baseURL = api.defaults.baseURL;
      const res = await fetch(`${baseURL}/sop-translations/${translationId}/audio`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      audio.src = URL.createObjectURL(blob);
      await audio.play();
      setState('playing');
    } catch (e) {
      setState('error');
      setErrorMsg(String(e?.message || e));
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={state === 'loading'}
      title={`Listen in ${langLabel}`}
      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium border transition disabled:opacity-60"
      style={{
        backgroundColor: state === 'playing' ? '#F5C86B' : state === 'error' ? '#FEF2F2' : '#fff',
        color: state === 'playing' ? '#8C560F' : state === 'error' ? '#D4726A' : '#2A7B88',
        borderColor: state === 'playing' ? '#F5C86B' : state === 'error' ? '#FECACA' : '#2A7B88',
      }}
      data-testid="sop-listen-btn"
    >
      {state === 'loading' ? <Loader2 size={11} className="animate-spin" /> :
        state === 'playing' ? <Pause size={11} /> : <Volume2 size={11} />}
      {state === 'loading' ? 'Generating…' :
        state === 'playing' ? 'Pause' :
        state === 'error' ? `Retry (${errorMsg.slice(0, 22)})` :
        'Listen'}
    </button>
  );
}

function SopReadModal({ item, onClose }) {
  const [viewData, setViewData] = useState(null);
  const [lang, setLang] = useState('en');
  const [langLoading, setLangLoading] = useState(false);
  const [ackBusy, setAckBusy] = useState(false);
  const [ackError, setAckError] = useState('');
  const [acked, setAcked] = useState(!!item.acknowledged_at);
  // Track per-media playback progress (0..1) so we can disable "Mark as read"
  // until every audio/video is heard end-to-end.
  const [mediaProgress, setMediaProgress] = useState({});

  const fetchView = async (l) => {
    setLangLoading(true);
    try {
      const r = await api.get(`/me/training/view/${item.sop_id}?lang=${l}`);
      setViewData(r.data);
      return r.data;
    } finally { setLangLoading(false); }
  };

  useEffect(() => { fetchView('en'); /* eslint-disable-next-line */ }, [item.sop_id]);

  // Poll when translation is still being generated in the background.
  useEffect(() => {
    if (!viewData?.translation_pending || lang === 'en') return undefined;
    let tries = 0;
    const maxTries = 60; // ~3 minutes @ 3s
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const r = await api.get(`/me/training/view/${item.sop_id}?lang=${lang}`);
        if (!r.data.translation_pending || tries >= maxTries) {
          setViewData(r.data);
          clearInterval(iv);
        }
      } catch (_) {
        if (tries >= maxTries) clearInterval(iv);
      }
    }, 3000);
    return () => clearInterval(iv);
  }, [viewData?.translation_pending, lang, item.sop_id]);

  const switchLang = async (l) => {
    setLang(l);
    await fetchView(l);
  };

  const requiredIds = viewData?.required_media_ids || [];
  const missingCount = requiredIds.filter(id => (mediaProgress[id] || 0) < 0.95).length;
  const allHeard = missingCount === 0;

  const reportProgress = async (mediaId, percent) => {
    if (percent >= 0.95) {
      try { await api.post(`/me/training/${item.sop_id}/media-progress`, { media_id: mediaId, percent: percent * 100 }); }
      catch (_) { /* non-fatal */ }
    }
  };

  const acknowledge = async () => {
    setAckBusy(true); setAckError('');
    try { await api.post(`/me/training/${item.sop_id}/acknowledge`); setAcked(true); }
    catch (e) {
      setAckError(e?.response?.data?.detail || 'Could not mark as read. Please try again.');
    }
    finally { setAckBusy(false); }
  };

  const unacknowledge = async () => {
    setAckBusy(true);
    try { await api.post(`/me/training/${item.sop_id}/unacknowledge`); setAcked(false); }
    finally { setAckBusy(false); }
  };

  // Reader watermark string
  const today = new Date().toISOString().slice(0, 10);
  const readerName = viewData?.reader?.name || 'Authorised Reader';
  const watermarkText = `Confidential · ${readerName} · ${today} · MindEscapes®`;
  const canDownload = viewData?.can_download;
  const media = viewData?.media || [];
  const audioVideo = media.filter(m => m.type === 'audio' || m.type === 'video');
  const images = media.filter(m => m.type === 'image');

  // Block right-click + text selection for non-leader readers.
  const protect = !canDownload;

  // Block Ctrl+P / Ctrl+S / Ctrl+Shift+S shortcuts while viewer is open for non-leaders.
  useEffect(() => {
    if (!protect) return undefined;
    const handler = (e) => {
      const k = (e.key || '').toLowerCase();
      if ((e.ctrlKey || e.metaKey) && (k === 'p' || k === 's' || k === 'c')) {
        e.preventDefault(); e.stopPropagation();
      }
      if (k === 'printscreen') { e.preventDefault(); }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [protect]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-2" onClick={onClose}>
      <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col relative" onClick={e => e.stopPropagation()} data-testid="my-sop-read-modal">
        {/* Header */}
        <div className="p-4 border-b" style={{ borderColor: '#E5E1DC' }}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{viewData?.title || item.title}</p>
              <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>{item.category} · {item.outlet_name}</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => {
                  const title = viewData?.title || item.title;
                  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://scaleboard.in';
                  const url = `${origin}/my-training?open=${item.sop_id || item.id}`;
                  const msg = [
                    `🎙️ *${title}*${item.category ? ` · ${item.category}` : ''}`,
                    '',
                    'Listen in your language (தமிழ் / हिन्दी / नेपाली / ಕನ್ನಡ):',
                    url,
                    '',
                    '— MindEscapes Training',
                  ].join('\n');
                  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
                }}
                className="text-[10px] px-2 py-0.5 rounded-full border font-medium inline-flex items-center gap-1"
                style={{ borderColor: '#86EFAC', color: '#166534', backgroundColor: '#ECFDF5' }}
                title="Share this training on WhatsApp — recipient gets a link to listen in 4 languages"
                data-testid="sop-whatsapp-share"
              >
                <MessageCircle size={10} /> Share
              </button>
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }} title="Viewing only — not downloadable">
                <Lock size={9} /> Confidential
              </span>
            </div>
          </div>
          {/* Language toggle */}
          {viewData?.supported_langs && (
            <div className="flex items-center gap-1 mt-3 flex-wrap" data-testid="sop-lang-toggle">
              <Languages size={12} style={{ color: '#9CA3AF' }} />
              {Object.entries(viewData.supported_langs).map(([code, label]) => (
                <button
                  key={code}
                  type="button"
                  disabled={langLoading}
                  onClick={() => switchLang(code)}
                  className="text-[10px] px-2 py-0.5 rounded-full font-medium border transition disabled:opacity-50"
                  style={{
                    backgroundColor: lang === code ? '#2A7B88' : '#fff',
                    color: lang === code ? '#fff' : '#1B2A4A',
                    borderColor: lang === code ? '#2A7B88' : '#E5E1DC',
                  }}
                  data-testid={`sop-lang-${code}`}
                >
                  {label}
                </button>
              ))}
              {langLoading && <span className="text-[10px]" style={{ color: '#9CA3AF' }}>translating…</span>}
              {/* 🔊 Listen — plays ElevenLabs TTS narration for the active translation */}
              {lang !== 'en' && viewData?.translation_id && viewData?.translated && (
                <SopAudioPlayer translationId={viewData.translation_id} langLabel={viewData.supported_langs?.[lang] || lang} />
              )}
            </div>
          )}
        </div>

        {/* Body — content with watermark overlay */}
        <div
          className="overflow-y-auto flex-1 p-4 space-y-3 relative sop-viewer-protected"
          onContextMenu={protect ? (e) => e.preventDefault() : undefined}
          style={protect ? { userSelect: 'none', WebkitUserSelect: 'none' } : undefined}
          data-testid="sop-viewer-body"
        >
          {/* Watermark overlay — repeating pattern across full content */}
          {protect && (
            <div
              className="pointer-events-none absolute inset-0 z-10"
              style={{
                backgroundImage: `repeating-linear-gradient(-30deg, transparent 0 120px, rgba(212,114,106,0.08) 120px 240px)`,
              }}
              aria-hidden
            >
              <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                <div className="w-[200%] text-center select-none" style={{ transform: 'rotate(-20deg)', color: 'rgba(27,42,74,0.07)', fontSize: 20, fontWeight: 700, lineHeight: '4em', letterSpacing: '0.1em' }}>
                  {Array.from({ length: 30 }).map((_, i) => (<div key={i}>{watermarkText}</div>))}
                </div>
              </div>
            </div>
          )}
          {viewData?.translation_error && (
            <div className="relative z-20 p-2 rounded text-[11px] font-medium" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }}>{viewData.translation_error}</div>
          )}
          {viewData?.translation_pending && lang !== 'en' && (
            <div className="relative z-20 p-2 rounded text-[11px] font-medium flex items-center gap-2" style={{ backgroundColor: '#FFFBEB', color: '#7C2D12' }} data-testid="translation-pending-banner">
              <Clock size={12} className="animate-pulse" />
              Translating to {viewData.supported_langs?.[lang]?.split(' ')[0] || lang.toUpperCase()}… showing English master in the meantime.
            </div>
          )}
          {viewData?.content ? (
            viewData.structured_content ? (
              <StructuredConciergeModule view={viewData} />
            ) : (
              <div className="relative z-20 whitespace-pre-wrap text-sm leading-relaxed" style={{ color: '#1B2A4A' }} data-testid="sop-content">
                {viewData.content}
              </div>
            )
          ) : (
            <p className="relative z-20 text-xs" style={{ color: '#9CA3AF' }}>Loading content…</p>
          )}

          {/* Streaming audio/video players (ALL staff) — full playback required for ack */}
          {audioVideo.length > 0 && (
            <div className="relative z-20 pt-3 border-t space-y-2" style={{ borderColor: '#F3F4F6' }}>
              <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: '#9CA3AF' }}>
                Required — listen/watch fully ({audioVideo.filter(m => (mediaProgress[m.id] || 0) >= 0.95).length}/{audioVideo.length} complete)
              </p>
              {audioVideo.map((m, i) => {
                const p = mediaProgress[m.id] || 0;
                const pct = Math.round(p * 100);
                const done = p >= 0.95;
                const mediaProps = {
                  src: m.url,
                  controls: true,
                  controlsList: 'nodownload noplaybackrate',
                  onContextMenu: (e) => e.preventDefault(),
                  onTimeUpdate: (e) => {
                    const el = e.currentTarget;
                    if (el.duration && !isNaN(el.duration)) {
                      const cur = el.currentTime / el.duration;
                      setMediaProgress(prev => {
                        const prior = prev[m.id] || 0;
                        const next = Math.max(prior, cur);
                        if (prior < 0.95 && next >= 0.95) reportProgress(m.id, next);
                        return { ...prev, [m.id]: next };
                      });
                    }
                  },
                  onEnded: () => {
                    setMediaProgress(prev => ({ ...prev, [m.id]: 1 }));
                    reportProgress(m.id, 1);
                  },
                  style: { width: '100%' },
                };
                return (
                  <div key={m.id || i} className="rounded-md border p-2" style={{ borderColor: done ? '#86EFAC' : '#E5E1DC', backgroundColor: done ? '#F0FDF4' : '#FFF' }} data-testid={`sop-media-${m.type}-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium truncate" style={{ color: '#1B2A4A' }}>
                        {m.type === 'video' ? '▶' : '♪'} {m.name || `${m.type} ${i + 1}`}
                      </p>
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: done ? '#DCFCE7' : '#FEF3C7', color: done ? '#166534' : '#92400E' }} data-testid={`media-status-${i}`}>
                        {done ? '✓ Completed' : pct === 0 ? 'Not started' : `In Progress · ${pct}%`}
                      </span>
                    </div>
                    {m.type === 'audio' ? <audio {...mediaProps} /> : <video {...mediaProps} />}
                    <div className="h-1 rounded-full mt-1" style={{ backgroundColor: '#F3F4F6' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: done ? '#2D8B5E' : '#D4726A' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Image attachments (streaming, no download hint) */}
          {images.length > 0 && (
            <div className="relative z-20 pt-3 border-t grid grid-cols-2 gap-2" style={{ borderColor: '#F3F4F6' }}>
              {images.map((m, i) => (
                <img key={i} src={m.url} alt={m.name || 'SOP image'} className="rounded-md border" style={{ borderColor: '#E5E1DC' }} onContextMenu={protect ? (e) => e.preventDefault() : undefined} draggable={false} data-testid={`sop-image-${i}`} />
              ))}
            </div>
          )}

          {/* Leader-only downloadable source files */}
          {canDownload && (viewData?.document_url || media.length > 0) && (
            <div className="relative z-20 pt-3 border-t" style={{ borderColor: '#F3F4F6' }}>
              <p className="text-[10px] uppercase tracking-wide font-semibold mb-1" style={{ color: '#9CA3AF' }}>Leader downloads (you only)</p>
              {viewData.document_url && (
                <a href={viewData.document_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs px-3 py-1.5 rounded border mb-1" style={{ borderColor: '#E5E1DC', color: '#378ADD' }} data-testid="leader-download-primary">
                  <Download size={11} /> Primary document
                </a>
              )}
              {media.map((m, i) => (
                <a key={i} href={m.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs px-3 py-1.5 rounded border mb-1" style={{ borderColor: '#E5E1DC', color: '#378ADD' }} data-testid={`leader-download-media-${i}`}>
                  <Download size={11} /> {m.name || m.url.split('/').pop()}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Footer: read-only notice + CTA */}
        <div className="border-t" style={{ borderColor: '#E5E1DC' }}>
          {lang !== 'en' && viewData?.translated && (
            <div className="px-3 py-1.5 text-[10px] text-center" style={{ backgroundColor: '#FFFBEB', color: '#7C2D12' }}>
              This is an auto-translated aid. <strong>English remains the authoritative version</strong>
              {viewData.translation_cached ? '' : ' · newly translated'}.
            </div>
          )}
          <div className="p-3" style={{ backgroundColor: '#FAF9F7' }}>
            {acked ? (
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs flex items-center gap-1.5" style={{ color: '#166534' }}>
                  <CheckCircle2 size={14} /> You've marked this as read.
                </p>
                <button onClick={unacknowledge} disabled={ackBusy} className="text-[11px] underline" style={{ color: '#6B7280' }} data-testid="unacknowledge-btn">
                  Undo
                </button>
              </div>
            ) : (
              <>
                {!allHeard && (
                  <p className="text-[11px] mb-2 flex items-center gap-1.5" style={{ color: '#92400E' }} data-testid="audio-gate-hint">
                    <Clock size={11} /> Listen to {missingCount} more audio/video item{missingCount === 1 ? '' : 's'} fully to unlock "Mark as read".
                  </p>
                )}
                {ackError && (
                  <p className="text-[11px] mb-2" style={{ color: '#991B1B' }} data-testid="ack-error">{ackError}</p>
                )}
                <button onClick={acknowledge} disabled={ackBusy || !allHeard}
                  className="w-full py-2.5 rounded-md text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: allHeard ? '#2A7B88' : '#9CA3AF' }}
                  data-testid="acknowledge-btn">
                  {ackBusy ? 'Saving…' : allHeard ? "I've read this — mark as complete" : 'In Progress — complete all audio/video first'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Concierge training programme — 5 waves, soft-recommend order, no hard locks ──
function ConciergeProgramme({ items, onOpen }) {
  const WAVE_DEFS = [
    { n: 1, name: 'Brand & Culture',      intro: 'Who we are. Read first.',                          accent: '#2A7B88' },
    { n: 2, name: 'The Craft',            intro: 'Request handling · discretion · trust.',           accent: '#3B5BA9' },
    { n: 3, name: 'Lifestyle Verticals',  intro: 'Dining · travel · experiences · gifting.',         accent: '#7B5EA7' },
    { n: 4, name: 'Operations',           intro: 'The scaffolding — vendors · CRM · channels.',      accent: '#BA7517' },
    { n: 5, name: 'Brand Overlays',       intro: 'Les Concierges vs Club Concierge specifics.',      accent: '#2D8B5E' },
  ];

  const byWave = {};
  for (const it of items) {
    const w = it.wave || 1;
    (byWave[w] = byWave[w] || []).push(it);
  }
  Object.values(byWave).forEach(arr => arr.sort((a, b) =>
    (a.sequence_in_wave || 0) - (b.sequence_in_wave || 0) ||
    (a.module_code || '').localeCompare(b.module_code || '')
  ));

  const total = items.length;
  const done = items.filter(i => !!i.acknowledged_at).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const [expanded, setExpanded] = useState({ 1: true, 2: false, 3: false, 4: false, 5: false });
  const toggle = (n) => setExpanded(prev => ({ ...prev, [n]: !prev[n] }));

  // First Wave-1 module that's still pending = recommended next read
  const wave1Pending = (byWave[1] || []).filter(i => !i.acknowledged_at);
  const recommendedNext = wave1Pending[0] || (byWave[2] || []).find(i => !i.acknowledged_at) || null;

  return (
    <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC' }} data-testid="concierge-programme">
      <div className="p-4 border-b" style={{ borderColor: '#E5E1DC', backgroundColor: '#F7F5F2' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold tracking-wide uppercase" style={{ color: '#6B7280' }}>Concierge Training Programme</p>
            <p className="text-base font-bold mt-0.5" style={{ color: '#1B2A4A' }}>Les Concierges® · Club Concierge®</p>
            <p className="text-[11px] italic mt-0.5" style={{ color: '#6B7280' }}>
              "Care that leaves an impression without leaving a trace."
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-2xl font-bold leading-none" style={{ color: '#2D8B5E' }}>{pct}%</p>
            <p className="text-[10px]" style={{ color: '#6B7280' }}>{done}/{total} complete</p>
          </div>
        </div>
        <div className="mt-3 h-1.5 rounded-full" style={{ backgroundColor: '#E5E1DC' }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: '#2D8B5E' }} />
        </div>
        {recommendedNext && (
          <button
            onClick={() => onOpen(recommendedNext)}
            className="mt-3 w-full text-left px-3 py-2.5 rounded-md border-2 hover:bg-white transition flex items-center justify-between gap-2"
            style={{ borderColor: '#2A7B88', backgroundColor: '#F0F9F9' }}
            data-testid="recommended-next">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#2A7B88' }}>
                {recommendedNext.wave === 1 ? '★ Start here' : 'Continue'} · {recommendedNext.module_code}
              </p>
              <p className="text-sm font-medium mt-0.5 truncate" style={{ color: '#1B2A4A' }}>{recommendedNext.title}</p>
            </div>
            <span className="text-xs flex-shrink-0" style={{ color: '#2A7B88' }}>Read →</span>
          </button>
        )}
      </div>

      <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
        {WAVE_DEFS.map(w => {
          const waveItems = byWave[w.n] || [];
          if (waveItems.length === 0) return null;
          const waveDone = waveItems.filter(i => !!i.acknowledged_at).length;
          const waveTotal = waveItems.length;
          const open = expanded[w.n];
          return (
            <div key={w.n}>
              <button
                onClick={() => toggle(w.n)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F7F5F2] transition text-left"
                data-testid={`wave-${w.n}-header`}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 text-white" style={{ backgroundColor: w.accent }}>
                  {w.n}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
                    Wave {w.n} · {w.name}
                  </p>
                  <p className="text-[11px]" style={{ color: '#6B7280' }}>{w.intro}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-semibold" style={{ color: waveDone === waveTotal ? '#2D8B5E' : '#6B7280' }}>
                    {waveDone}/{waveTotal}
                  </p>
                  <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{open ? 'Hide' : 'View'}</p>
                </div>
              </button>

              {open && (
                <div className="px-4 pb-3 space-y-1.5" style={{ backgroundColor: '#FAFAF7' }}>
                  {waveItems.map(it => {
                    const isDone = !!it.acknowledged_at;
                    const isStartHere = w.n === 1 && !isDone && it === wave1Pending[0];
                    return (
                      <button
                        key={it.assignment_id}
                        onClick={() => onOpen(it)}
                        className="w-full text-left px-3 py-2.5 rounded-md bg-white border hover:border-[#2A7B88] transition flex items-start gap-2.5"
                        style={{ borderColor: isStartHere ? '#2A7B88' : '#E5E1DC', borderWidth: isStartHere ? 2 : 1 }}
                        data-testid={`lcg-module-${it.module_code}`}>
                        {isDone ? (
                          <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#2D8B5E' }} />
                        ) : (
                          <div className="w-4 h-4 mt-0.5 rounded-full border-2 flex-shrink-0" style={{ borderColor: isStartHere ? '#2A7B88' : '#D4726A' }} />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] font-bold" style={{ color: w.accent }}>{it.module_code}</span>
                            {isStartHere && (
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: '#2A7B88' }}>
                                ★ START HERE
                              </span>
                            )}
                            {it.overdue && !isDone && (
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }}>
                                OVERDUE
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-medium mt-0.5" style={{ color: '#1B2A4A' }}>{it.title}</p>
                          {it.tagline && (
                            <p className="text-[11px] italic mt-0.5" style={{ color: '#6B7280' }}>{it.tagline}</p>
                          )}
                          <p className="text-[10px] mt-1" style={{ color: '#9CA3AF' }}>
                            {it.reading_time_min ? `${it.reading_time_min} min read` : '—'}
                            {it.word_count ? ` · ${it.word_count} words` : ''}
                            {isDone && it.acknowledged_at ? ` · read ${String(it.acknowledged_at).slice(0, 10)}` : ''}
                          </p>
                        </div>
                        <span className="text-[10px] flex-shrink-0" style={{ color: '#378ADD' }}>→</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="px-4 py-2.5 border-t flex items-center justify-between gap-2" style={{ borderColor: '#E5E1DC', backgroundColor: '#F7F5F2' }}>
        <p className="text-[10px]" style={{ color: '#6B7280' }}>
          Read in order for the best experience, but you may jump ahead to look up a module anytime.
        </p>
      </div>
    </div>
  );
}

// ─── Structured 10-section renderer for concierge training modules ─────────────
function StructuredConciergeModule({ view }) {
  const sc = view.structured_content || {};
  const mc = view.module_code;
  const wave = view.wave;
  const waveName = view.wave_name;
  const tagline = view.tagline;

  const sections = [
    { key: 'why_this_matters',      label: '1. Why This Matters',       accent: '#2A7B88', type: 'prose' },
    { key: 'the_substance',         label: '2. The Substance',          accent: '#2A7B88', type: 'prose' },
    { key: 'common_mistakes',       label: '3. Common Mistakes',        accent: '#BA7517', type: 'prose' },
    { key: 'a_moment_from_the_work',label: '4. A Moment From the Work', accent: '#7B5EA7', type: 'story' },
    { key: 'practice_drill',        label: '5. Practice Drill',         accent: '#3B5BA9', type: 'prose' },
    { key: 'checklist_items',       label: '6. Checklist',              accent: '#2D8B5E', type: 'checklist' },
    { key: 'check_questions',       label: '7. Check Questions',        accent: '#2D8B5E', type: 'list' },
    { key: 'reflection_questions',  label: '8. Reflection Questions',   accent: '#6B7280', type: 'list' },
    { key: 'goal',                  label: '9. Goal',                   accent: '#1B2A4A', type: 'goal' },
  ];

  return (
    <div className="relative z-20 space-y-4" data-testid="lcg-structured-view">
      {/* Header card */}
      <div className="rounded-lg p-4 border" style={{ backgroundColor: '#F7F5F2', borderColor: '#E5E1DC' }}>
        <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#2A7B88' }}>
          {mc} · Wave {wave} · {waveName}
        </p>
        <p className="text-xl font-bold mt-1" style={{ color: '#1B2A4A' }}>{view.title}</p>
        {tagline && (
          <p className="text-sm italic mt-1.5 leading-relaxed" style={{ color: '#6B7280' }}>{tagline}</p>
        )}
        <p className="text-[11px] mt-2" style={{ color: '#9CA3AF' }}>
          {view.reading_time_min ? `${view.reading_time_min} min read` : ''}
          {view.word_count ? ` · ${view.word_count} words` : ''}
        </p>
      </div>

      {/* Section cards */}
      {sections.map(s => {
        const val = sc[s.key];
        const isEmpty = !val || (Array.isArray(val) && val.length === 0) || (typeof val === 'string' && !val.trim());
        if (isEmpty) return null;

        if (s.type === 'goal') {
          return (
            <div key={s.key} className="rounded-lg p-4 border-2" style={{ backgroundColor: '#F0F9F9', borderColor: '#2A7B88' }}>
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#2A7B88' }}>The Goal</p>
              <p className="text-sm mt-1 leading-relaxed font-medium" style={{ color: '#1B2A4A' }}>{val}</p>
            </div>
          );
        }

        if (s.type === 'story') {
          return (
            <div key={s.key} className="rounded-lg p-4 border-l-4" style={{ backgroundColor: '#FBFAF7', borderColor: s.accent }}>
              <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: s.accent }}>{s.label}</p>
              <div className="text-sm leading-relaxed italic whitespace-pre-wrap" style={{ color: '#1B2A4A' }}>{val}</div>
            </div>
          );
        }

        if (s.type === 'checklist') {
          return (
            <div key={s.key} className="rounded-lg p-4 border" style={{ borderColor: '#E5E1DC', backgroundColor: '#fff' }}>
              <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: s.accent }}>{s.label}</p>
              <ul className="space-y-1.5">
                {val.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#1B2A4A' }}>
                    <div className="w-4 h-4 mt-0.5 rounded border flex-shrink-0" style={{ borderColor: s.accent, backgroundColor: '#fff' }} />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        if (s.type === 'list') {
          return (
            <div key={s.key} className="rounded-lg p-4 border" style={{ borderColor: '#E5E1DC', backgroundColor: '#fff' }}>
              <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: s.accent }}>{s.label}</p>
              <ol className="space-y-2 pl-5 list-decimal">
                {val.map((q, i) => (
                  <li key={i} className="text-sm leading-relaxed" style={{ color: '#1B2A4A' }}>{q}</li>
                ))}
              </ol>
            </div>
          );
        }

        return (
          <div key={s.key} className="rounded-lg p-4 border" style={{ borderColor: '#E5E1DC', backgroundColor: '#fff' }}>
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: s.accent }}>{s.label}</p>
            <div className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#1B2A4A' }}>{val}</div>
          </div>
        );
      })}
    </div>
  );
}

